import React, { useCallback, useEffect, useState } from 'react';
import MaterialTable, { Column } from 'material-table';
import { Button } from '@material-ui/core';
import {
  loadUserSubmittedLabResults,
  setFilter,
  selectUserSubmittedListError,
  selectUserSubmittedLabResults,
  selectPage,
  selectLoading,
  selectFilter,
  selectDisableNext,
  updateUserSubmittedLabResultById,
  setAutomatedStatusChange,
  loadBiomarkersConfig,
  setShouldRedirect,
} from '../../state';
import { useDispatch, useSelector } from 'react-redux';
import useStyles from './UserSubmittedLabResultsList.styles';
import {
  ListItem,
  UserSubmittedLabResultsRequest,
} from '../../../../interfaces/user-submitted-lab-result';
import { format } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { selectLoggedIn } from '../../../auth/state';

export default function UserSubmittedLabResultsList() {
  const dispatch = useDispatch();
  const error = useSelector(selectUserSubmittedListError);
  const classes = useStyles();
  const data = useSelector(selectUserSubmittedLabResults);
  const loggedIn = useSelector(selectLoggedIn);
  const currentPage = useSelector(selectPage);
  const isLoading = useSelector(selectLoading);
  const filter = useSelector(selectFilter);
  const disableNext = useSelector(selectDisableNext);
  const history = useHistory();

  useEffect(() => {
    dispatch(loadUserSubmittedLabResults());
    dispatch(loadBiomarkersConfig());
    dispatch(setFilter('toDo'));
  }, []);

  const handlePrevButtonClick = () => {
    dispatch(loadUserSubmittedLabResults(false, true));
  };

  const handleNextButtonClick = () => {
    dispatch(loadUserSubmittedLabResults(true, false));
  };

  const handleFilterChange = (value: string) => {
    dispatch(setFilter(value));
    dispatch(loadUserSubmittedLabResults());
  };

  const handleReviewButtonClick = async (rowData: any) => {
    if (rowData.status === 'new' || rowData.status === 'forReview') {
      let newStatus = '';
      if (rowData.status === 'new') {
        dispatch(setAutomatedStatusChange(true));
        dispatch(setShouldRedirect(false));
        newStatus = 'inProgress';
      } else if (rowData.status === 'forReview') {
        dispatch(setAutomatedStatusChange(true));
        dispatch(setShouldRedirect(false));
        newStatus = 'inReview';
      }
      const savedData: UserSubmittedLabResultsRequest = {
        status: newStatus,
      };
      await dispatch(updateUserSubmittedLabResultById(rowData.userId, rowData.resultId, savedData));
      history.push(`/user-submitted-lab-results/${rowData.userId}/${rowData.resultId}/`);
    } else {
      history.push(`/user-submitted-lab-results/${rowData.userId}/${rowData.resultId}/`);
    }
  };

  if (error) {
    return <div className={classes.error}>{error}</div>;
  }

  const columns: Column<ListItem>[] = [
    {
      field: 'resultId',
      title: 'ID',
      sorting: false,
    },
    {
      field: 'uploadedOn',
      title: 'Uploaded On',
      render: (data: ListItem) => {
        if (data.uploadedOn) {
          try {
            return format(new Date(data.uploadedOn), 'dd/MM/yyyy, hh:mm aaa');
          } catch (e) {
            return data.uploadedOn;
          }
        }
        return data.uploadedOn;
      },
      sorting: false,
    },
    { field: 'assignee', title: 'Assignee', sorting: false },
    {
      field: 'status',
      title: 'Status',
      render: (data: ListItem) => {
        const result = data.status.replace(/([A-Z])/g, ' $1');
        const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
        return finalResult;
      },
      sorting: false,
    },
    ...(filter !== 'toDo' && filter !== 'rejected'
      ? [
          {
            field: 'completedOn',
            title: 'Completed On',
            render: (data: ListItem) => {
              if (data.completedOn) {
                try {
                  return format(new Date(data.completedOn), 'dd/MM/yyyy, hh:mm aaa');
                } catch (e) {
                  return data.completedOn;
                }
              }
              return data.completedOn;
            },
            sorting: false,
          },
        ]
      : []),
    ...(filter !== 'toDo' && filter !== 'completed'
      ? [
          {
            field: 'rejectedOn',
            title: 'Rejected On',
            render: (data: ListItem) => {
              if (data.rejectedOn) {
                try {
                  return format(new Date(data.rejectedOn), 'dd/MM/yyyy, hh:mm aaa');
                } catch (e) {
                  return data.rejectedOn;
                }
              }
              return data.rejectedOn;
            },
            sorting: false,
          },
        ]
      : []),
    {
      title: '',
      field: 'internal_action',
      render: (rowData: any) => {
        let textValue = '';
        let disabled = false;
        let email = '';
        if (loggedIn) {
          email = loggedIn.payload.username;
        }
        if (rowData.status === 'forReview') {
          disabled = email === rowData.formSubmittedBy;
          textValue = 'Review';
        } else if (rowData.status === 'new') {
          textValue = 'Start';
        } else if (rowData.status === 'inReview' || rowData.status === 'inProgress') {
          disabled = email === rowData.formSubmittedBy;
          textValue = 'Continue';
        } else if (rowData.status === 'rejected' || rowData.status === 'completed') {
          textValue = 'View';
        }
        return (
          <Button
            disabled={disabled}
            color="primary"
            onClick={() => handleReviewButtonClick(rowData)}
          >
            {textValue}
          </Button>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <Button
        variant="text"
        color="primary"
        disabled={filter === 'toDo'}
        onClick={() => handleFilterChange('toDo')}
      >
        To Do
      </Button>
      <Button
        variant="text"
        color="primary"
        disabled={filter === 'completed'}
        onClick={() => handleFilterChange('completed')}
      >
        Completed
      </Button>
      <Button
        variant="text"
        color="primary"
        disabled={filter === 'rejected'}
        onClick={() => handleFilterChange('rejected')}
      >
        Rejected
      </Button>
      <Button
        variant="text"
        color="primary"
        disabled={filter === 'all'}
        onClick={() => handleFilterChange('all')}
      >
        All Results
      </Button>
      <MaterialTable
        isLoading={isLoading}
        title="User-Submitted Lab Results"
        columns={columns}
        data={data.map(o => ({ ...o }))}
        options={{
          headerStyle: { position: 'sticky', top: 0 },
          maxBodyHeight: 'calc(100vh - 128px - 73px)',
          thirdSortClick: false,
          paging: false,
          debounceInterval: 500,
          search: false,
        }}
      />
      {currentPage !== 0 && (
        <Button color="primary" onClick={handlePrevButtonClick}>
          Prev
        </Button>
      )}
      {!disableNext && (
        <Button color="primary" onClick={handleNextButtonClick}>
          Next
        </Button>
      )}
    </React.Fragment>
  );
}
